import { injectable, inject } from 'inversify';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import type {
  IMarketArticlesBaseFilterPayload,
  IMarketArticlesBoundaryFilterPayload,
  IMarketArticlesFilterBoundaryResponse,
  IMarketArticlesResponse,
  IMarketArticlesTileBasePayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import { IMarketTheme } from '@/creditflowresearch/domain/vo/ArticlesAndStats/IMarketTheme';
import { ISingleArticle } from '@/condor/domain/vo/Finsight/CreditFlowResearch/IArticle';
import {
  IGetArticleDealsPayload,
  IGetArticleThemesPayload,
  IGetArticleCompaniesPayload,
  IGetArticleDealsResponse,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Article';
import Request from '@framework/api/Rpc/Request';
import ICompany from '@/creditflowresearch/domain/vo/ICompany';
import type { IMarketStatPayload, IMarketStatResponse } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketStat';
import type { IMarketCategory, IMarketCategoryPayload } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketCategory';
import { ISingleStat } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stat';
import { IDealPayload, IDealResponse } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IDeal';
import { IIssuerPayload, IIssuerResponse } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IIssuer';
import { IParentPayload, IParentResponse } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IParent';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';
import type { IServerCacheOptions } from '@/creditflowresearch/infrastructure/cache/withServerCache';

@injectable()
class CreditFlowResearchRepository {
  constructor(@inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher) {}

  // Articles

  getArticleByViewId = async (viewId: number): Promise<ISingleArticle> => {
    const request = new Request('finsight_web.market.research.article.get_by_view_id', { viewId });
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  getArticleDeals = async (payload: IGetArticleDealsPayload = {}): Promise<IGetArticleDealsResponse> => {
    const request = new Request('finsight_web.market.deal_transaction.deal.get_info', payload);
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  getArticleCompanies = async (payload: IGetArticleCompaniesPayload): Promise<ICompany[]> => {
    const request = new Request('finsight_web.market.company.get_info_by_ids', payload);
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  @withServerCache({
    scope: 'CreditFlowResearch',
    ttlSeconds: 180,
    maxEntries: 25,
    anonymousOnly: true,
  })
  async getArticlesList(
    payload: IMarketArticlesTileBasePayload,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need for decorator
    _cacheOptions?: IServerCacheOptions,
  ): Promise<IMarketArticlesResponse> {
    const request = new Request('cfr.article.get_all', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  getArticlesFilters = async (
    payload: IMarketArticlesBoundaryFilterPayload,
  ): Promise<IMarketArticlesFilterBoundaryResponse> => {
    const request = new Request('finsight_web.market.research.article.filter.get_boundary_filter', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getFiltersByHash = async (hash: string): Promise<IMarketArticlesBaseFilterPayload> => {
    const request = new Request('finsight_web.market.research.article.filter.get_filter_by_hash', { hash });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getDealsList = async (payload: IDealPayload): Promise<IDealResponse> => {
    const request = new Request('finsight_web.market.research.article.filter.deal.list', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getIssuersList = async (payload: IIssuerPayload): Promise<IIssuerResponse> => {
    const request = new Request('finsight_web.market.research.article.filter.issuer.list', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getParentsList = async (payload: IParentPayload): Promise<IParentResponse> => {
    const request = new Request('finsight_web.market.research.article.filter.parent.list', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getMarketThemesList = async (payload: IGetArticleThemesPayload = {}): Promise<IMarketTheme[]> => {
    let request = new Request('finsight_web.market.research.theme.get_all', payload);
    let response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  getAttachmentDownloadUrl = async (
    { uploadId, displayName }: { uploadId: string, displayName: string },
  ): Promise<{ url: string }> => {
    let request = new Request('finsight_web.market.attachment.get_downloading_url', { uploadId, displayName });
    let response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };

  // Stats

  @withServerCache({
    scope: 'CreditFlowResearch',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getStatsList(
    payload: IMarketStatPayload,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need for decorator
    _cacheOptions?: IServerCacheOptions,
  ): Promise<IMarketStatResponse> {
    const request = new Request('finsight_web.market.research.stat.get_all', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  @withServerCache({
    scope: 'CreditFlowResearch',
    ttlSeconds: 180,
    anonymousOnly: true,
  })
  async getMarketCategoriesList(
    payload: IMarketCategoryPayload,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Need for decorator
    _cacheOptions?: IServerCacheOptions,
  ): Promise<IMarketCategory[]> {
    const request = new Request('finsight_web.market.research.stat_category.get_all', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  }

  getStatByViewId = async (viewId: number): Promise<ISingleStat> => {
    const request = new Request('finsight_web.market.research.stat.get_by_view_id', { viewId });
    const response = await this.rpc.call<RpcSuccess>(request);
    return response.getResult().payload;
  };
}

export default CreditFlowResearchRepository;
